'use client';

import React, { useState, useEffect } from 'react';
import CalendarHeader from './CalendarHeader';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DayView from './DayView';
import EventCard from './EventCard';
import FilterPanel from './FilterPanel';
import CountdownBanner from './CountdownBanner';
import EventModal from './EventModal';

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  type: 'preparation' | 'celebration' | 'community' | 'global';
  description: string;
  organizer: string;
  attendees: number;
  image: string;
  alt: string;
  timezone: string;
  region: string;
  fullDescription: string;
  resources: string[];
}

// Helper: get date string offset from today
function getDateFromToday(offsetDays: number): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(today);
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().split('T')[0];
}

const GlobalCalendarInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentView, setCurrentView] = useState<'month' | 'week' | 'day'>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Daily rituals: 30-day journey starting from today (Day 1 = today)
  const dailyRituals: CalendarEvent[] = [
  {
    id: 'ritual-1',
    title: 'Day 1: Emotional Baseline',
    date: getDateFromToday(0),
    time: '08:00',
    location: 'Personal Practice',
    type: 'preparation',
    description: 'Identify your dominant emotions and their immediate triggers across five life areas: teamwork, romantic relationships, marriage, workplace conflict, and unemployment.',
    organizer: 'SIG Day Global Team',
    attendees: 15420,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_164f3b9c2-1768146724230.png",
    alt: 'Person journaling in a quiet space reflecting on emotions and daily experiences',
    timezone: 'Local',
    region: 'Global',
    fullDescription: `**Day 1: Emotional Baseline**\n\nObjective: Identify your dominant emotions and their immediate triggers across five life areas.\n\nHow to do it:\n• Find a quiet space and open your journal\n• Reflect on each of the five scenarios below\n• Note your emotional response and what triggered it\n\n**Scenario 1 – Teamwork:** A colleague takes credit for your idea in a meeting. What do you feel? What does it trigger?\n\n**Scenario 2 – Love Affairs:** Your partner cancels plans last minute without explanation. What emotion arises immediately?\n\n**Scenario 3 – Marriage:** Your spouse dismisses your concerns during an argument. What do you feel in your body?\n\n**Scenario 4 – Workplace Conflict:** Your manager publicly criticizes your work. What is your first emotional reaction?\n\n**Scenario 5 – Unemployment:** You are passed over for a promotion despite your efforts. What emotion dominates?\n\nJournaling Action: Write one sentence for each scenario: "When [situation], I feel [emotion] because [belief/trigger]."\n\nInsight: Your emotional baseline is not a weakness — it is your starting point for transformation.`,
    resources: [
    'Day 1 Emotional Baseline PDF Guide',
    'Journaling Prompts Workbook',
    '30-Day Preparation Guide',
    'Daily Meditation Audio Series']

  },
  {
    id: 'ritual-2',
    title: 'Day 2: Identifying Your Enemies',
    date: getDateFromToday(1),
    time: '08:00',
    location: 'Personal Practice',
    type: 'preparation',
    description: 'Reflect on relationships where hostility exists. Write down names and situations that trigger negative emotions.',
    organizer: 'SIG Day Global Team',
    attendees: 14800,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17bc7406a-1772151780618.png",
    alt: 'Person sitting alone in contemplation near a window with soft natural light',
    timezone: 'Local',
    region: 'Global',
    fullDescription: `**Day 2: Identifying Your Enemies**\n\nObjective: Bring awareness to the relationships and situations where hostility, resentment, or conflict lives.\n\nHow to do it:\n• In your journal, list up to 5 people or situations that trigger negative emotions\n• For each, note: What happened? How long have you carried this? What emotion does it bring?\n\nJournaling Action: "The person/situation I find hardest to forgive is _____ because _____. I have carried this for _____.\n\nInsight: You cannot heal what you refuse to name. Naming is the first act of courage.`,
    resources: ['Conflict Awareness Worksheet', 'Forgiveness Framework Workbook']
  },
  {
    id: 'ritual-3',
    title: 'Day 3: The Cost of Hostility',
    date: getDateFromToday(2),
    time: '08:00',
    location: 'Personal Practice',
    type: 'preparation',
    description: 'Examine how carrying grudges affects your mental health, relationships, and overall well-being.',
    organizer: 'SIG Day Global Team',
    attendees: 14200,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1284f0601-1773816846201.png",
    alt: 'Person looking stressed with hands on head, representing the weight of unresolved conflict',
    timezone: 'Local',
    region: 'Global',
    fullDescription: `**Day 3: The Cost of Hostility**\n\nObjective: Understand the physical, emotional, and relational price of holding onto resentment.\n\nHow to do it:\n• Read about the neuroscience of grudges and chronic stress\n• Reflect on how hostility has affected your health, sleep, and relationships\n• Identify one area of your life most impacted by unresolved conflict\n\nJournaling Action: "Carrying this resentment has cost me _____ in my [health/relationships/peace of mind]."\n\nInsight: Forgiveness is not a gift to the other person — it is medicine for yourself.`,
    resources: ['Neuroscience of Forgiveness Article', 'Stress & Conflict Research Summary']
  },
  {
    id: 'ritual-4',
    title: 'Day 4: Compassion Meditation',
    date: getDateFromToday(3),
    time: '08:00',
    location: 'Personal Practice',
    type: 'preparation',
    description: 'Guided meditation focusing on developing compassion for yourself and others, including those who have hurt you.',
    organizer: 'SIG Day Global Team',
    attendees: 13900,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1125e445d-1772195188050.png",
    alt: 'Person meditating peacefully in a serene outdoor setting with eyes closed',
    timezone: 'Local',
    region: 'Global',
    fullDescription: `**Day 4: Compassion Meditation**\n\nObjective: Begin cultivating compassion — the antidote to hostility.\n\nHow to do it:\n• Find 15 minutes of quiet time\n• Follow the guided loving-kindness meditation\n• Direct compassion first to yourself, then to loved ones, then to neutral people, then to those who have hurt you\n\nJournaling Action: "After today's meditation, I noticed _____ when I directed compassion toward [difficult person]."\n\nInsight: Compassion does not excuse harmful behavior — it frees you from being controlled by it.`,
    resources: ['Guided Compassion Meditation Audio', 'Loving-Kindness Practice Guide']
  },
  {
    id: 'ritual-5',
    title: 'Day 5: Community Connection',
    date: getDateFromToday(4),
    time: '18:00',
    location: 'Local Chapter / Online',
    type: 'community',
    description: 'Join a local SIG Day chapter or online community. Share your journey and learn from others.',
    organizer: 'SIG Day Global Team',
    attendees: 13500,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_118a73fe7-1766520699886.png",
    alt: 'Diverse group of people gathered in community circle sharing stories and connecting',
    timezone: 'Local',
    region: 'Global',
    fullDescription: `**Day 5: Community Connection**\n\nObjective: Break isolation and discover that your journey is shared by thousands worldwide.\n\nHow to do it:\n• Find your local SIG Day chapter or join the online community\n• Introduce yourself and share one insight from your first 4 days\n• Listen to one other person's story with full presence\n\nJournaling Action: "What I shared with my community today was _____. What I received was _____.\n\nInsight: Healing accelerates in community. You are not alone in this journey.`,
    resources: ['Community Organizer Toolkit', 'Online Community Access Guide']
  },
  {
    id: 'ritual-6',
    title: 'Day 6: Forgiveness Letter Writing',
    date: getDateFromToday(5),
    time: '08:00',
    location: 'Personal Practice',
    type: 'preparation',
    description: 'Write a letter to someone who hurt you (not to send). Express your feelings and begin the release process.',
    organizer: 'SIG Day Global Team',
    attendees: 13100,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c0a74dbe-1772147533398.png",
    alt: 'Person writing thoughtfully in a journal with pen in hand at a wooden desk',
    timezone: 'Local',
    region: 'Global',
    fullDescription: `**Day 6: Forgiveness Letter Writing**\n\nObjective: Give voice to your pain as a step toward releasing it.\n\nHow to do it:\n• Choose one person from your Day 2 list\n• Write them a letter — do NOT send it\n• Express everything: the hurt, the anger, the grief, and what you needed\n• End with one sentence beginning: "I am choosing to release this because..."\n\nJournaling Action: Notice what emotions arose during writing. What surprised you?\n\nInsight: Writing externalizes what has been trapped inside. The letter is for you, not them.`,
    resources: ['Forgiveness Letter Template', 'Emotional Release Techniques Guide']
  },
  {
    id: 'ritual-7',
    title: 'Day 7: Weekly Reflection',
    date: getDateFromToday(6),
    time: '10:00',
    location: 'Personal Practice',
    type: 'preparation',
    description: 'Review your first week. What emotions arose? What resistance did you feel? Celebrate small victories.',
    organizer: 'SIG Day Global Team',
    attendees: 12800,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ac90d1e9-1772075802684.png",
    alt: 'Person reviewing journal notes at a calm desk with morning light streaming in',
    timezone: 'Local',
    region: 'Global',
    fullDescription: `**Day 7: Weekly Reflection**\n\nObjective: Consolidate your first week of transformation work.\n\nHow to do it:\n• Re-read your journal entries from Days 1–6\n• Answer: What shifted? What surprised me? Where did I resist?\n• Celebrate one small victory from this week\n\nJournaling Action: "This week I discovered _____ about myself. My biggest challenge was _____. I am proud of _____.\n\nInsight: Transformation is not linear. Every step — including the hard ones — is progress.`,
    resources: ['Weekly Reflection Worksheet', '30-Day Journey Progress Tracker']
  },
  {
    id: 'ritual-14',
    title: 'Day 14: Mid-Journey Reflection',
    date: getDateFromToday(13),
    time: '10:00',
    location: 'Personal Practice',
    type: 'preparation',
    description: 'Assess your transformation at the halfway point. What has shifted? What still feels difficult? Recommit to the journey.',
    organizer: 'SIG Day Global Team',
    attendees: 11200,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ac90d1e9-1772075802684.png",
    alt: 'Person sitting at a desk with open journal and a cup of tea, reflecting on their journey',
    timezone: 'Local',
    region: 'Global',
    fullDescription: `**Day 14: Mid-Journey Reflection**\n\nObjective: Pause at the halfway point to assess your transformation and renew your commitment.\n\nHow to do it:\n• Review your emotional baseline from Day 1\n• Compare: How do you feel about the same five scenarios now?\n• Identify what has shifted and what still needs work\n\nJournaling Action: "Halfway through this journey, I have changed in _____. I still struggle with _____. I recommit to _____ for the next 16 days."\n\nInsight: The middle of the journey is where most people quit. Staying is the practice.`,
    resources: ['Mid-Journey Assessment Tool', 'Recommitment Ceremony Guide']
  },
  {
    id: 'ritual-community-ny',
    title: 'Community Healing Circle - New York',
    date: getDateFromToday(7),
    time: '18:00',
    location: 'Central Park, New York, USA',
    type: 'community',
    description: 'Local gathering for shared stories, collective healing, and building connections across differences.',
    organizer: 'NYC Peace Builders',
    attendees: 342,
    image: 'https://images.unsplash.com/photo-1506605944214-e4fc22d021e2',
    alt: 'Multicultural group of people holding hands in circle outdoors in park setting',
    timezone: 'EST',
    region: 'North America',
    fullDescription: `Experience the power of community healing in this in-person gathering at Central Park. We'll create a safe space for:\n\n• Sharing personal transformation stories\n• Practicing forgiveness exercises together\n• Building lasting connections across cultural divides\n• Planning April 17th local celebration\n\nBring a blanket, an open heart, and willingness to connect authentically. Light refreshments provided.`,
    resources: [
    'Healing Circle Facilitation Guide',
    'Conflict Resolution Techniques',
    'Community Building Activities']

  },
  {
    id: 'ritual-forgiveness-london',
    title: 'Forgiveness Workshop - London',
    date: getDateFromToday(15),
    time: '14:00',
    location: 'Peace Hall, London, UK',
    type: 'preparation',
    description: 'Deep dive into forgiveness practices with expert facilitators and psychological research insights.',
    organizer: 'UK Peace Institute',
    attendees: 156,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17b023f41-1765904328251.png',
    alt: 'Professional woman leading workshop presentation to attentive audience in modern conference room',
    timezone: 'GMT',
    region: 'Europe',
    fullDescription: `This intensive workshop explores the science and practice of forgiveness through:\n\n• Research-backed forgiveness frameworks\n• Guided exercises for personal healing\n• Understanding the neuroscience of compassion\n• Tools for releasing resentment and anger\n\nLed by Dr. Sarah Mitchell, clinical psychologist specializing in trauma recovery and forgiveness therapy.`,
    resources: [
    'Forgiveness Science Research Papers',
    'Personal Healing Workbook',
    'Trauma-Informed Practices Guide']

  },
  {
    id: 'ritual-youth-dar',
    title: 'Youth Peace Summit - Dar es Salaam',
    date: getDateFromToday(23),
    time: '09:00',
    location: 'Dar es Salaam Convention Center, Tanzania',
    type: 'global',
    description: 'Young leaders from across Africa gathering to design peace-building initiatives for their communities.',
    organizer: 'African Youth Peace Network',
    attendees: 487,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_14a417c28-1768846473254.png',
    alt: 'Young diverse students engaged in group discussion at conference table with laptops and notebooks',
    timezone: 'EAT',
    region: 'Africa',
    fullDescription: `Empowering the next generation of peace builders through:\n\n• Youth-led peace project presentations\n• Skill-building workshops on conflict resolution\n• Networking with international youth leaders\n• Funding opportunities for community initiatives\n\nThis summit brings together 500+ young leaders aged 16-30 from 25 African nations.`,
    resources: [
    'Youth Leadership Toolkit',
    'Project Funding Application Guide',
    'Peer-to-Peer Organizing Manual']

  },
  {
    id: 'ritual-interfaith-mumbai',
    title: 'Interfaith Dialogue - Mumbai',
    date: getDateFromToday(10),
    time: '16:00',
    location: 'Unity Temple, Mumbai, India',
    type: 'community',
    description: 'Religious leaders from diverse traditions exploring common ground in forgiveness and compassion.',
    organizer: 'Mumbai Interfaith Council',
    attendees: 234,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1da4f1e1f-1766558787871.png',
    alt: 'Religious leaders from different faiths sitting together in peaceful discussion in ornate temple setting',
    timezone: 'IST',
    region: 'Asia',
    fullDescription: `A sacred gathering of faith leaders exploring universal themes of forgiveness:\n\n• Shared wisdom from multiple religious traditions\n• Collaborative prayer and meditation practices\n• Building bridges across faith communities\n• Planning interfaith April 17th celebrations\n\nFeaturing speakers from Hindu, Muslim, Christian, Buddhist, Sikh, and Jain traditions.`,
    resources: [
    'Interfaith Dialogue Guidelines',
    'Sacred Texts on Forgiveness',
    'Multi-Faith Ceremony Planning']

  },
  {
    id: 'ritual-april17',
    title: 'April 17th Global Celebration',
    date: '2026-04-17',
    time: '00:00',
    location: 'Worldwide - All Time Zones',
    type: 'celebration',
    description: 'The main event! 24 hours of global unity, forgiveness ceremonies, and choosing peace over hostility.',
    organizer: 'SIG Day Foundation',
    attendees: 2847563,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_166a55988-1767531767330.png',
    alt: 'Large crowd of people celebrating together with raised hands at outdoor festival during sunset',
    timezone: 'All Zones',
    region: 'Global',
    fullDescription: `April 17th marks the culmination of our collective journey - a day when millions worldwide choose to live with "no enemy." Join the global celebration:\n\n• Sunrise forgiveness ceremonies in every time zone\n• 24-hour live stream connecting communities worldwide\n• Local festivals celebrating peace and unity\n• Humanitarian action projects supporting vulnerable populations\n• Personal pledge ceremonies and community gatherings\n\nThis is not just an event - it's a movement toward sustainable peace through individual transformation.`,
    resources: [
    'April 17th Celebration Guide',
    'Local Event Planning Toolkit',
    'Forgiveness Ceremony Scripts',
    'Social Media Campaign Materials',
    'Press Kit and Media Resources']

  }];


  const mockEvents = dailyRituals;

  const handleViewChange = (view: 'month' | 'week' | 'day') => {
    setCurrentView(view);
  };

  const handleNavigate = (direction: 'prev' | 'next' | 'today') => {
    if (!isHydrated) return;

    const newDate = new Date(currentDate);

    if (direction === 'today') {
      setCurrentDate(new Date());
      return;
    }

    if (currentView === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    } else if (currentView === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    }

    setCurrentDate(newDate);
  };

  const handleDateClick = (date: Date) => {
    setCurrentDate(date);
    setCurrentView('day');
  };

  const handleEventClick = (eventId: string) => {
    const event = mockEvents.find((e) => e.id === eventId);
    if (event) {
      setSelectedEvent(event);
      setIsModalOpen(true);
    }
  };

  const handleTypeToggle = (type: string) => {
    setSelectedTypes((prev) =>
    prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleRegionToggle = (region: string) => {
    setSelectedRegions((prev) =>
    prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region]
    );
  };

  const handleClearFilters = () => {
    setSelectedTypes([]);
    setSelectedRegions([]);
  };

  const handleRegister = (eventId: string) => {
    alert(`Registration successful for event ${eventId}! You will receive a confirmation email shortly.`);
  };

  const handleShare = (eventId: string) => {
    if (!isHydrated) return;

    const event = mockEvents.find((e) => e.id === eventId);
    if (event && navigator.share) {
      navigator.share({
        title: event.title,
        text: event.description,
        url: window.location.href
      }).catch(() => {
        alert('Event link copied to clipboard!');
      });
    } else {
      alert('Event link copied to clipboard!');
    }
  };

  const filteredEvents = mockEvents.filter((event) => {
    const typeMatch = selectedTypes.length === 0 || selectedTypes.includes(event.type);
    const regionMatch = selectedRegions.length === 0 || selectedRegions.includes(event.region);
    return typeMatch && regionMatch;
  });

  const upcomingEvents = filteredEvents.
  filter((event) => new Date(event.date) >= new Date()).
  sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).
  slice(0, 6);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="h-20 bg-muted rounded-lg"></div>
            <div className="h-96 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <CountdownBanner />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <FilterPanel
              selectedTypes={selectedTypes}
              selectedRegions={selectedRegions}
              onTypeToggle={handleTypeToggle}
              onRegionToggle={handleRegionToggle}
              onClearFilters={handleClearFilters} />

          </div>

          {/* Main Calendar Area */}
          <div className="lg:col-span-3">
            <CalendarHeader
              currentView={currentView}
              currentDate={currentDate}
              onViewChange={handleViewChange}
              onNavigate={handleNavigate} />


            {currentView === 'month' &&
            <MonthView
              currentDate={currentDate}
              events={filteredEvents}
              onDateClick={handleDateClick}
              onEventClick={handleEventClick} />

            }

            {currentView === 'week' &&
            <WeekView
              currentDate={currentDate}
              events={filteredEvents}
              onEventClick={handleEventClick} />

            }

            {currentView === 'day' &&
            <DayView
              currentDate={currentDate}
              events={filteredEvents}
              onEventClick={handleEventClick} />

            }
          </div>
        </div>

        {/* Upcoming Events Section */}
        <div className="mt-12">
          <h2 className="text-3xl font-headline font-bold text-foreground mb-6">
            Upcoming Daily Rituals & Events
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcomingEvents.map((event) =>
            <EventCard
              key={event.id}
              event={event}
              onRegister={handleRegister}
              onShare={handleShare} />

            )}
          </div>
        </div>
      </div>

      <EventModal
        event={selectedEvent}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onRegister={handleRegister}
        onShare={handleShare} />

    </div>);

};

export default GlobalCalendarInteractive;